import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { ActivityType, AnyEntry, Intake } from '../types'
import { useUser } from '../hooks/useUser'
import { listKeyForType } from '../utils/entries'
import { verifyPassword, requestPasswordReset } from '../utils/api'
import { showNotification } from '../utils/notifications'
import NavBar, { type DashboardView } from '../components/layout/NavBar'
import PasswordLock from '../components/layout/PasswordLock'
import PasswordReset from '../components/layout/PasswordReset'
import OnboardingWizard from '../components/onboarding/OnboardingWizard'
import History from '../components/history/History'
import Analytics from '../components/analytics/Analytics'
import WorkoutReport from '../components/report/WorkoutReport'
import SettingsModal, { type SettingsSection } from '../components/settings/SettingsModal'
import EntryForm, { type FormMode } from '../components/entries/EntryForm'
import FoodForm, { type FoodMode } from '../components/intake/FoodForm'

interface FormState {
  open: boolean
  type: ActivityType
  mode: FormMode
  index: number | null
  seed: AnyEntry | null
}

const CLOSED: FormState = { open: false, type: 'cardio', mode: 'new', index: null, seed: null }

interface FoodState {
  open: boolean
  mode: FoodMode
  index: number | null
  seed: Intake | null
}

const FOOD_CLOSED: FoodState = { open: false, mode: 'new', index: null, seed: null }

export default function Dashboard() {
  const params = useParams<{ userId: string; '*': string }>()
  const navigate = useNavigate()
  const userId = params.userId
  const sub = params['*']
  // Recomputed each render; cleared once we navigate away from ?reset=.
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const resetToken = searchParams.get('reset')
  const showWelcome = searchParams.get('welcome') === '1'
  const user = useUser(userId)
  const [view, setView] = useState<DashboardView>(
    sub === 'stats' ? 'stats' : sub === 'report' ? 'report' : 'home',
  )
  const [form, setForm] = useState<FormState>(CLOSED)
  const [food, setFood] = useState<FoodState>(FOOD_CLOSED)
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(null)
  const [locked, setLocked] = useState(false)
  const [lockIsTimeout, setLockIsTimeout] = useState(false)

  const hasPassword = !!user.data.passwordHash
  const passwordMode = user.data.passwordMode
  const timeoutMinutes = user.data.timeoutMinutes

  // Decide the initial lock state once the record has loaded.
  useEffect(() => {
    if (!user.isInitialized || !userId) return
    if (!hasPassword) {
      setLocked(false)
      return
    }
    if (passwordMode === 'after_timeout') {
      setLocked(false) // open now; the inactivity timer locks later
    } else {
      const unlocked = sessionStorage.getItem(`wlns:unlocked:${userId}`) === '1'
      setLockIsTimeout(false)
      setLocked(!unlocked)
    }
  }, [user.isInitialized, hasPassword, passwordMode, userId])

  // Inactivity auto-lock for the after_timeout mode.
  useEffect(() => {
    if (!hasPassword || passwordMode !== 'after_timeout' || locked) return
    const mins = timeoutMinutes && timeoutMinutes > 0 ? timeoutMinutes : 5
    let timer: ReturnType<typeof setTimeout>
    const reset = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        setLockIsTimeout(true)
        setLocked(true)
      }, mins * 60_000)
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [hasPassword, passwordMode, timeoutMinutes, locked])

  const handleUnlock = async (pw: string): Promise<boolean> => {
    if (!userId) return false
    const res = await verifyPassword(userId, pw)
    if (res.valid) {
      user.cachePassword(pw)
      sessionStorage.setItem(`wlns:unlocked:${userId}`, '1')
      setLocked(false)
      return true
    }
    return false
  }

  const handleForgot = async () => {
    if (userId) await requestPasswordReset(userId)
  }

  const handleResetDone = (pw: string) => {
    if (!userId) return
    user.cachePassword(pw)
    sessionStorage.setItem(`wlns:unlocked:${userId}`, '1')
    setLocked(false)
    navigate(`/u/${userId}`, { replace: true }) // strip ?reset=
  }

  const cancelReset = () => {
    if (userId) navigate(`/u/${userId}`, { replace: true })
  }

  const lockNow = () => {
    if (!userId) return
    sessionStorage.removeItem(`wlns:unlocked:${userId}`)
    user.clearPassword()
    setLockIsTimeout(false)
    setLocked(true)
  }

  const seedFor = (type: ActivityType, index: number): AnyEntry | null => {
    const list = user.data.lists[listKeyForType(type)] as AnyEntry[]
    return list?.[index] ?? null
  }

  const openNew = (type: ActivityType) =>
    setForm({ open: true, type, mode: 'new', index: null, seed: null })
  const openEdit = (type: ActivityType, index: number) =>
    setForm({ open: true, type, mode: 'edit', index, seed: seedFor(type, index) })
  const openCopy = (type: ActivityType, index: number) =>
    setForm({ open: true, type, mode: 'copy', index: null, seed: seedFor(type, index) })

  const handleSave = (type: ActivityType, entry: AnyEntry, index: number | null) => {
    if (index !== null) {
      user.updateEntry(type, index, entry)
      showNotification('Entry updated')
    } else {
      user.addEntry(type, entry)
      showNotification('Entry saved')
    }
    setForm(CLOSED)
  }

  const handleDelete = (type: ActivityType, index: number) => {
    if (!window.confirm('Delete this entry?')) return
    user.deleteEntry(type, index)
    showNotification('Entry deleted')
  }

  // ---- Food & Drink (Intake) ----
  const intakeAt = (id: number): Intake | null => user.data.intake?.[id] ?? null

  const openNewFood = () => setFood({ open: true, mode: 'new', index: null, seed: null })
  const openEditFood = (id: number) => setFood({ open: true, mode: 'edit', index: id, seed: intakeAt(id) })
  const openCopyFood = (id: number) => setFood({ open: true, mode: 'copy', index: null, seed: intakeAt(id) })

  const handleSaveFood = (intake: Intake, index: number | null) => {
    if (index !== null) {
      user.updateIntake(index, intake)
      showNotification('Food updated')
    } else {
      user.addIntake(intake)
      showNotification('Food logged')
    }
    setFood(FOOD_CLOSED)
  }

  const handleDeleteIntake = (id: number) => {
    if (!window.confirm('Delete this entry?')) return
    user.deleteIntake(id)
    showNotification('Entry deleted')
  }

  if (user.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (showWelcome && userId) {
    return <OnboardingWizard userId={userId} user={user} onDone={() => navigate(`/u/${userId}`, { replace: true })} />
  }

  if (resetToken && userId) {
    return <PasswordReset userId={userId} token={resetToken} onDone={handleResetDone} onCancel={cancelReset} />
  }

  if (locked) {
    return <PasswordLock onUnlock={handleUnlock} isTimeout={lockIsTimeout} onForgot={handleForgot} />
  }

  return (
    <div className="flex-1 flex flex-col">
      <NavBar
        data={user.data}
        view={view}
        onLog={openNew}
        onLogFood={openNewFood}
        onSetView={setView}
        onOpenSettings={setSettingsSection}
        onLock={hasPassword ? lockNow : undefined}
      />

      <main className="max-w-2xl w-full mx-auto px-4 pb-24 flex-1">
        {user.error && (
          <div className="card toast-error mt-4 text-sm">{user.error}</div>
        )}
        {user.loadMode === 'offline-cache' && (
          <div className="card mt-4 text-sm text-[var(--accent-orange)]">
            Showing offline cache — changes will sync when you reconnect.
          </div>
        )}

        {view === 'home' && (
          <History
            data={user.data}
            onEdit={openEdit}
            onCopy={openCopy}
            onDelete={handleDelete}
            onEditIntake={openEditFood}
            onCopyIntake={openCopyFood}
            onDeleteIntake={handleDeleteIntake}
          />
        )}
        {view === 'stats' && <Analytics data={user.data} />}
        {view === 'report' && <WorkoutReport data={user.data} />}
      </main>

      <EntryForm
        type={form.type}
        mode={form.mode}
        index={form.index}
        seed={form.seed}
        isOpen={form.open}
        data={user.data}
        onClose={() => setForm(CLOSED)}
        onSave={handleSave}
      />

      <FoodForm
        isOpen={food.open}
        mode={food.mode}
        seed={food.seed}
        index={food.index}
        data={user.data}
        onClose={() => setFood(FOOD_CLOSED)}
        onSave={handleSaveFood}
      />

      <SettingsModal
        section={settingsSection}
        onClose={() => setSettingsSection(null)}
        user={user}
        onLock={hasPassword ? lockNow : undefined}
      />

      <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--border)] bg-[var(--bg-secondary)]/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-2 px-4 py-2 text-xs text-[var(--text-secondary)]">
          <span className="font-display">
            © 2026 <span className="font-extrabold uppercase text-[var(--text-primary)]">WLNS</span>
            <span className="font-light">.app</span>
          </span>
          <nav className="flex gap-4">
            <Link to="/features" className="hover:text-[var(--text-primary)]">Features</Link>
            <Link to="/security" className="hover:text-[var(--text-primary)]">Security</Link>
            <Link to="/contact" className="hover:text-[var(--text-primary)]">Contact</Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}
